import { Box, Container, Typography } from "@mui/material";
import { Cart, NextPageWithLayout } from "@cm-websites/common";
import { cmsApiClient, CmsApiAcl } from "@cm-websites/common/dist/cms-api";
import { NavigationAndFooterLayout } from "../layouts";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import React from "react";
import type { CmsApiModel } from "@cm-websites/common/dist/cms-api";
import type { GetStaticProps } from "next";
import type { Product } from "@cm-websites/common";

type CartProps = { products: Product[] };

const CartPage: NextPageWithLayout<CartProps> = ({ products }) => (
  <React.Fragment>
    <NextSeo title="Warenkorb | Culinario Mortale®" noindex />

    <Box sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: { xs: 0, sm: 8, lg: 14, xl: 16 } }}>
      <Container maxWidth="xl">
        <Typography variant="h1">Warenkorb</Typography>
      </Container>
      <Cart products={products} sx={{ mt: 6 }} />
    </Box>
  </React.Fragment>
);

CartPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const fields = [
    "*.*",
    "relatedCases.related_Culinario_Mortale_id.*.*",
    "printVersionImages.*",
    "printVersionImages.directus_files_id.mask",
    "printVersionImages.directus_files_id.id",
    "printVersionImages.directus_files_id.description",
    "onlineVersionImages.*",
    "onlineVersionImages.directus_files_id.mask",
    "onlineVersionImages.directus_files_id.id",
    "onlineVersionImages.directus_files_id.description",
  ];

  const { data } = await cmsApiClient.get<{
    data: CmsApiModel.components["schemas"]["ItemsCulinarioMortale"][];
  }>(`/items/Culinario_Mortale?filter={"language": "DE"}&fields=${fields.join(",")}`);

  const printProducts = await Promise.all(
    data.data.map((p) => CmsApiAcl.product(p, "print", { origin: "warenkorb print" }))
  );
  const onlineProducts = await Promise.all(
    data.data.map((p) => CmsApiAcl.product(p, "online", { origin: "warenkorb online" }))
  );

  const products = [...printProducts, ...onlineProducts].sort((a, b) => {
    const nameA = a.id.toUpperCase(); // ignore upper and lowercase
    const nameB = b.id.toUpperCase(); // ignore upper and lowercase

    // sort in an descending order
    if (nameA < nameB) {
      return 1;
    }
    if (nameA > nameB) {
      return -1;
    }

    // names must be equal
    return 0;
  });

  return {
    props: {
      ...(locale ? await serverSideTranslations(locale, ["c-common", "c-cart"], i18nConfig) : {}),
      products,
    },
  };
};

export default CartPage;
